/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  FaArrowLeft,
  FaMicrophone,
  // FaMicrophoneSlash,
  FaStop,
  FaStopwatch,
  FaVolumeUp,
  FaCamera,
  FaCameraRetro,
  FaRedo,
  FaSpinner,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaCheck,
} from 'react-icons/fa';
import { MdSkipNext, MdCheck, MdWarning } from 'react-icons/md';
import { IoMdPause, IoMdPlay } from 'react-icons/io';
import Webcam from 'react-webcam';
import { useNavigate, useParams } from 'react-router-dom';
import { FormInputs } from '../../../types/session-forms.types';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firebase-firestore';
import 'firebase/storage';
import { Collections } from '../../../types/collection';
import api from '../../../libs/axiosInstance';
import * as faceapi from 'face-api.js';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Question } from '../../../interfaces/question.interface';
import { RunData } from '../../../interfaces/run.interface';
import Countdown from '../components/Countdown';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useUserContext } from '../../../contexts/user.context';
import { INTERVIEW_TYPES } from '../../../pages/NewSession';
import Confetti from 'react-confetti';
import { sessionCompletionService } from '../../../services/session-completion.service';
import { motion, AnimatePresence } from 'framer-motion';
import { useWindowSize } from 'react-use';
import { captureException } from '@sentry/react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Subscriptions } from '../../payments/enhanced-pricing-plans.type';
import { apiUsageService } from '../../../services/api-usage.service';

export interface ExpressionData {
  angry: number;
  disgusted: number;
  fearful: number;
  happy: number;
  neutral: number;
  sad: number;
  surprised: number;
}

// Handle browser speech recognition APIs
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const browserSpeechRecognition =
  (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

// Define speech synthesis API
const speechSynthesis = window.speechSynthesis;

// ElevenLabs voice IDs - these are some of their premium voices
const ELEVENLABS_VOICES = {
  rachel: "21m00Tcm4TlvDq8ikWAM", // Rachel - warm, natural female
  adam: "pNInz6obpgDQGcFmaJgB",   // Adam - professional male
  antoni: "ErXwobaYiN019PkySvjV", // Antoni - warm male
  bella: "EXAVITQu4vr4xnSDxMaL",  // Bella - natural female
  sam: "yoZ06aMxZJJ28mfd3POQ",    // Sam - casual conversational
};

// Define event types with wildcards to avoid compatibility issues
interface SimpleEventData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// Type of speech recognition object - simplified to avoid browser compatibility issues
interface BrowserSpeechRecognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onresult: (event: SimpleEventData) => void;
  onerror: (event: SimpleEventData) => void;
}

const VideoActiveSession: React.FC = () => {
  const { user, reloadUserData } = useUserContext();
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const { sessionId, runId } = useParams();
  const [sessionData, setSessionData] = useState<FormInputs>();
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);

  // --- Recording states ---
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const webcamRef = useRef<Webcam>(null);
  const recordedBlobsRef = useRef<Blob[]>([]);
  const [expressions, setExpressions] = useState<ExpressionData[]>([]);
  const [submitting, toggleSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showRunCompleteDialog, toggleShowRunCompleteDialog] = useState(false);
  const analysisIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // New state variables
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);
  const [, setUploadProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const audioAnalyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioDataRef = useRef<Uint8Array | null>(null);
  const [permissionDialogVisible, setPermissionDialogVisible] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownValue, setCountdownValue] = useState(3);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [cameraErrorMsg, setCameraErrorMsg] = useState<string | null>(null);
  const [microphoneErrorMsg, setMicrophoneErrorMsg] = useState<string | null>(null);
  const [isVideoPlayable, setIsVideoPlayable] = useState(false);

  // Overall session countdown (not the recording limit).
  // <Countdown ... />

  // --- Recording Timer states ---
  const MAX_RECORDING_TIME_MS =
    Number(user?.subscription.recordingLength) * 60 * 1000; // e.g., 10 minutes => 600000 ms

  const [recordingTimeLeft, setRecordingTimeLeft] = useState(
    MAX_RECORDING_TIME_MS / 1000
  );

  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // For Confetti sizing
  const { width, height } = useWindowSize();

  // New state variables for background processing
  const [backgroundUploads, setBackgroundUploads] = useState<{ [key: string]: { progress: number, status: 'uploading' | 'completed' | 'error' } }>({});
  const [showUploadInfo, setShowUploadInfo] = useState(false);
  const uploadInfoTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  // New state variables for text-to-speech
  const [isReading, setIsReading] = useState(false);
  const [isLoadingVoice, setIsLoadingVoice] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // const speechSynthesisRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const intentionalCancelRef = useRef(false);
  // Track if current question has been auto-read
  const hasAutoReadRef = useRef<number>(-1);

  // Add state for interviewer speaking animation
  const [interviewerSpeaking, setInterviewerSpeaking] = useState(false);

  // New state variables for improvements
  const [isLoading, setIsLoading] = useState(true);
  const [showTips, setShowTips] = useState(true);
  const [isEditingTranscript, setIsEditingTranscript] = useState(false);
  const [editedTranscript, setEditedTranscript] = useState('');
  const [autoReadEnabled, setAutoReadEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  // ---------------------------------------------
  // Helper function to format time as mm:ss
  // ---------------------------------------------
  const formatTime = (totalSeconds: number) => {
    // Ensure we're working with a valid number
    if (isNaN(totalSeconds) || totalSeconds < 0) {
      console.warn('Invalid time value:', totalSeconds);
      return "0:00"; // Return a default value
    }

    // Convert to integers to avoid floating point issues
    const totalSecondsInt = Math.floor(totalSeconds);
    const minutes = Math.floor(totalSecondsInt / 60);
    const seconds = totalSecondsInt % 60;
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  useEffect(() => {
    // Reload user data to ensure we get the updated subscription info
    reloadUserData();
  }, []);

  /* -----------------------------------------------------
   * CLEANUP: Stop all media streams on component unmount
   * This prevents the camera indicator from staying on after navigation
   * ----------------------------------------------------- */
  useEffect(() => {
    return () => {
      // Stop webcam stream
      if (webcamRef.current?.stream) {
        webcamRef.current.stream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      // Stop media recorder if active
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }

      // Stop speech recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors when stopping recognition
        }
      }

      // Close audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      // Clear all intervals
      if (analysisIntervalRef.current) {
        clearInterval(analysisIntervalRef.current);
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearTimeout(countdownIntervalRef.current);
      }
      if (uploadInfoTimeoutRef.current) {
        clearTimeout(uploadInfoTimeoutRef.current);
      }

      // Stop any playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [mediaRecorder]);

  /* -----------------------------------------------------
   * Load face-api models
   * ----------------------------------------------------- */
  useEffect(() => {
    const loadModels = async () => {
      const MODEL_URL = '/models';
      await faceapi.loadTinyFaceDetectorModel(MODEL_URL);
      await faceapi.loadFaceExpressionModel(MODEL_URL);
    };
    loadModels();
  }, []);

  /* -----------------------------------------------------
   * Fetch Session Data
   * ----------------------------------------------------- */
  useEffect(() => {
    async function getSessionData() {
      try {
        const userId = firebase.auth().currentUser?.uid;
        if (!userId) return;
        firebase
          .firestore()
          .collection(Collections.Sessions)
          .doc(userId)
          .collection('list')
          .doc(sessionId!)
          .onSnapshot((snapshot) => {
            if (!snapshot.exists) {
              setIsLoading(false);
              return;
            }
            const data = snapshot.data() as FormInputs;
            setSessionData({ ...data, id: snapshot.id });
            setIsLoading(false);
          });
      } catch (error) {
        captureException(error)
      }
    }
    getSessionData();
  }, [sessionId]);

  /* -----------------------------------------------------
   * Fetch Session Questions (Runs)
   * ----------------------------------------------------- */
  useEffect(() => {
    async function getSessionQuestions() {
      try {
        const userId = firebase.auth().currentUser?.uid;
        if (!userId) return;
        firebase
          .firestore()
          .collection(Collections.Runs)
          .doc(sessionId)
          .collection('list')
          .doc(runId)
          .onSnapshot((snapshot) => {
            if (!snapshot.exists) {
              setIsLoading(false);
              return;
            }
            const runData = snapshot.data() as RunData;
            if (runData.completed) {
              toggleShowRunCompleteDialog(true);
            }
            const dataQuestions = runData.questions?.sort((a, b) => a.id - b.id);
            setSessionQuestions(dataQuestions);
            setCurrentQuestion(runData.currentQuestion || 0);
            setTimeRemaining(runData.timeRemaining || 0);
            setIsLoading(false);
          });
      } catch (error) {
        captureException(error)
      }
    }
    getSessionQuestions();
  }, [sessionId, runId]);

  /* -----------------------------------------------------
   * Initialize SpeechRecognition
   * ----------------------------------------------------- */
  useEffect(() => {
    if (!browserSpeechRecognition) {
      captureException('SpeechRecognition is not supported in this browser.')
      return;
    }

    const recognition = new browserSpeechRecognition();
    recognition.lang = window.navigator.language || 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptChunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcriptChunk;
        } else {
          interimTranscript += transcriptChunk;
        }
      }
      setTranscript(finalTranscript + interimTranscript);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      captureException(event.error)
    };

    recognitionRef.current = recognition;
  }, []);

  /* -----------------------------------------------------
   * Start Recording
   * ----------------------------------------------------- */
  const startRecognition = () => {
    // Always try to use the webcam stream directly - this ensures we have permission
    if (webcamRef.current?.stream) {
      setHasPermission(true);

      // If we have a stream, we have permission
      setExpressions([]);
      recordedBlobsRef.current = [];
      setTranscript('');
      setVideoUrl(null);
      setIsVideoPlayable(false);

      // Use the webcam stream for audio setup
      setupAudioAnalyzer(webcamRef.current.stream);

      // Only start speech recognition if it's not already running
      // First make sure to stop any existing instance to avoid errors
      if (recognitionRef.current) {
        try {
          // Stop the current recognition if it's running
          if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
            // Wait a small delay before starting again
            setTimeout(() => {
              if (recognitionRef.current) {
                recognitionRef.current.start();
                setIsListening(true);
              }
            }, 100);
          } else {
            // If not listening, we can start directly
            recognitionRef.current.start();
            setIsListening(true);
          }
        } catch (err) {
          console.error("Speech recognition error:", err);
          // If there's an error, recreate the recognition object
          if (!browserSpeechRecognition) {
            return;
          }

          const recognition = new browserSpeechRecognition();
          recognition.lang = window.navigator.language || 'en-US';
          recognition.continuous = true;
          recognition.interimResults = true;

          recognition.onresult = (event: any) => {
            let interimTranscript = '';
            let finalTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
              const transcriptChunk = event.results[i][0].transcript;
              if (event.results[i].isFinal) {
                finalTranscript += transcriptChunk;
              } else {
                interimTranscript += transcriptChunk;
              }
            }
            setTranscript(finalTranscript + interimTranscript);
          };

          recognition.onerror = (event: any) => {
            captureException(event.error);
          };

          recognitionRef.current = recognition;
          recognition.start();
          setIsListening(true);
        }
      }

      // Start the media recording
      if (!isRecording) {
        const stream = webcamRef.current.stream;

        let options: MediaRecorderOptions = { mimeType: 'video/mp4' };
        if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
          options = { mimeType: 'video/webm; codecs=vp8' };
        }

        try {
          const recorder = new MediaRecorder(stream, options);

          recorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
              recordedBlobsRef.current.push(event.data);
            }
          };

          recorder.onstop = () => {
            const superBuffer = new Blob(recordedBlobsRef.current, {
              type: recorder.mimeType,
            });
            const videoURL = window.URL.createObjectURL(superBuffer);
            setVideoUrl(videoURL);
            setMediaRecorder(null);
            setIsVideoPlayable(true);

            // Play a completion sound
            const audio = new Audio('/sounds/recording-complete.mp3');
            audio.play().catch(e => console.error('Audio play error:', e));

            toast.current?.show({
              severity: 'success',
              summary: 'Recording Complete',
              detail: 'Your answer has been recorded. You can now review or submit it.',
            });
          };

          recorder.onpause = () => {
            setIsPaused(true);
          };

          recorder.onresume = () => {
            setIsPaused(false);
          };

          recorder.start(100); // timeslice of 100ms
          setMediaRecorder(recorder);
          setIsRecording(true);
          setIsPaused(false);

          // Start audio level monitoring
          setupAudioAnalyzer(stream);

          // Play a start sound
          const audio = new Audio('/sounds/recording-start.mp3');
          audio.play().catch(e => console.error('Audio play error:', e));

          // Start analyzing expressions
          startExpressionAnalysis();

          // Clear any existing intervals/timeouts
          if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
          }
          if (recordingTimeoutRef.current) {
            clearTimeout(recordingTimeoutRef.current);
            recordingTimeoutRef.current = null;
          }

          // Reset countdown to full duration
          setRecordingTimeLeft(MAX_RECORDING_TIME_MS / 1000);

          // (A) setInterval to update UI every second
          recordingIntervalRef.current = setInterval(() => {
            setRecordingTimeLeft((prev) => {
              // Ensure we're working with a valid number
              const current = isNaN(prev) ? MAX_RECORDING_TIME_MS / 1000 : prev;

              if (current <= 1) {
                stopRecognition();
                return 0;
              }
              return current - 1;
            });
          }, 1000);

          // (B) setTimeout to force-stop after max duration
          recordingTimeoutRef.current = setTimeout(() => {
            stopRecognition();
            stopExpressionAnalysis();
            submitAnswer();
          }, MAX_RECORDING_TIME_MS);

          toast.current?.show({
            severity: 'info',
            summary: 'Recording Started',
            detail: 'You are now recording your answer. Press space or the stop button when done.',
          });
        } catch (err) {
          captureException(err);
          toast.current?.show({
            severity: 'error',
            summary: 'Recording Error',
            detail: 'An error occurred while trying to record. Please try again.',
          });
        }
      }
    } else {
      // No stream available, we need to request permission
      toast.current?.show({
        severity: 'error',
        summary: 'Permission Required',
        detail: 'Camera and microphone permissions are required for recording.',
      });
      setPermissionDialogVisible(true);

      // Try to get permissions again
      navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        .then(() => {
          setHasPermission(true);
          // Force refresh the webcam component
          setTimeout(() => {
            if (webcamRef.current) {
              // Force webcam to reconnect
              const temp = webcamRef.current;
              temp.forceUpdate();
            }
          }, 500);
        })
        .catch((error) => {
          console.error("Permission error:", error);
          setHasPermission(false);
          setCameraErrorMsg("Camera access denied");
          setMicrophoneErrorMsg("Microphone access denied");
        });
    }
  };

  /* -----------------------------------------------------
   * Toggle Pause Recording
   * ----------------------------------------------------- */
  const togglePauseRecording = () => {
    if (!mediaRecorder) return;

    if (isPaused) {
      mediaRecorder.resume();
      setIsPaused(false);

      if (recognitionRef.current && isListening) {
        recognitionRef.current.start();
      }

      // Resume timers
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTimeLeft((prev) => {
          if (prev <= 1) {
            stopRecognition();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      toast.current?.show({
        severity: 'info',
        summary: 'Recording Resumed',
        detail: 'Your recording has been resumed.',
      });
    } else {
      mediaRecorder.pause();
      setIsPaused(true);

      if (recognitionRef.current && isListening) {
        recognitionRef.current.stop();
      }

      // Pause timers
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }

      toast.current?.show({
        severity: 'info',
        summary: 'Recording Paused',
        detail: 'Your recording is paused. Press resume to continue.',
      });
    }
  };

  /* -----------------------------------------------------
   * Stop Recording
   * ----------------------------------------------------- */
  const stopRecognition = () => {
    // Clear the interval & timeout so they don't fire again
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    // Reset the UI timer
    setRecordingTimeLeft(MAX_RECORDING_TIME_MS / 1000);

    // Stop SpeechRecognition if active
    if (recognitionRef.current) {
      try {
        if (isListening) {
          recognitionRef.current.stop();
          setIsListening(false);
        }
      } catch (err) {
        console.error("Error stopping speech recognition:", err);
      }
    }

    // Stop the MediaRecorder if recording
    if (isRecording && mediaRecorder) {
      mediaRecorder.requestData(); // Force a 'dataavailable' event
      mediaRecorder.stop();
      setIsRecording(false);
      setIsPaused(false);
    }

    // Stop analyzing expressions
    stopExpressionAnalysis();
  };

  /* -----------------------------------------------------
   * Start / Stop Expression Analysis
   * ----------------------------------------------------- */
  const startExpressionAnalysis = () => {
    if (analysisIntervalRef.current) return; // already running
    analysisIntervalRef.current = setInterval(async () => {
      if (
        webcamRef.current?.video &&
        webcamRef.current.video.readyState === 4 &&
        faceapi.nets.tinyFaceDetector.params
      ) {
        const detections = await faceapi
          .detectAllFaces(
            webcamRef.current.video,
            new faceapi.TinyFaceDetectorOptions()
          )
          .withFaceExpressions();
        if (detections && detections.length > 0) {
          setExpressions((prev) => [
            ...prev,
            detections[0].expressions as ExpressionData,
          ]);
        }
      }
    }, 1000);
  };

  const stopExpressionAnalysis = () => {
    if (analysisIntervalRef.current) {
      clearInterval(analysisIntervalRef.current);
      analysisIntervalRef.current = null;
    }
  };

  /* -----------------------------------------------------
   * Submit Answer
   * ----------------------------------------------------- */
  const submitAnswer = async () => {
    try {
      if (!sessionData || !sessionQuestions) return;
      toggleSubmitting(true);
      setUploadProgress(0);

      const userId = firebase.auth().currentUser?.uid;
      if (!userId) return;

      // Construct the final Blob
      const finalBlob = new Blob(recordedBlobsRef.current, {
        type: mediaRecorder?.mimeType || 'video/webm',
      });

      if (finalBlob.size === 0) {
        toggleSubmitting(false);
        toast.current?.show({
          severity: 'warn',
          summary: '🎥 No Recording Found',
          detail: 'Please record your answer before submitting. Click the start button to begin recording.',
          life: 4000,
        });
        return;
      }

      const formData = new FormData();
      formData.append('file', finalBlob, 'recording.mp4');
      formData.append('transcript', isEditingTranscript && editedTranscript ? editedTranscript : transcript);
      formData.append(
        'questionId',
        String(sessionQuestions[currentQuestion]?.id)
      );
      formData.append('expressions', JSON.stringify(expressions));
      formData.append('userId', userId);
      formData.append('sessionId', String(sessionData.id));
      formData.append('interviewType', INTERVIEW_TYPES.VIDEO_INTERVIEW);
      formData.append('runId', String(runId));

      // Add to background uploads
      const uploadId = sessionQuestions?.[currentQuestion]?.id
        ? `${sessionQuestions[currentQuestion].id}-${Date.now()}`
        : `unknown-${Date.now()}`;
      setBackgroundUploads(prev => ({
        ...prev,
        [uploadId]: { progress: 0, status: 'uploading' }
      }));

      // Show upload info after 3 seconds if still uploading
      uploadInfoTimeoutRef.current = setTimeout(() => {
        setShowUploadInfo(true);
      }, 3000);

      // Use axios with upload progress tracking
      const response = await api.post('/ai/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 100)
          );
          setUploadProgress(percentCompleted);
          setBackgroundUploads(prev => ({
            ...prev,
            [uploadId]: { ...prev[uploadId], progress: percentCompleted }
          }));
        },
      });

      if ([200, 201].includes(response.status)) {
        // Clear any existing toast
        toast.current?.clear();

        // Update background upload status
        setBackgroundUploads(prev => ({
          ...prev,
          [uploadId]: { ...prev[uploadId], status: 'completed' }
        }));

        // Clear upload info timeout if it exists
        if (uploadInfoTimeoutRef.current) {
          clearTimeout(uploadInfoTimeoutRef.current);
          uploadInfoTimeoutRef.current = null;
        }

        toast.current?.show({
          severity: 'success',
          summary: 'Success!',
          detail: 'Your response has been submitted and is being analyzed.',
        });

        // Play success sound
        const audio = new Audio('/sounds/success.mp3');
        audio.play().catch(e => console.error('Audio play error:', e));

        // If we're not already navigating, proceed to next question
        if (!isNavigating) {
          setIsNavigating(true);
          gotoNextQuestion();
        }
      }
      toggleSubmitting(false);
    } catch (error) {
      toggleSubmitting(false);
      captureException(error);

      // Update background upload status to error
      const uploadId = sessionQuestions?.[currentQuestion]?.id
        ? `${sessionQuestions[currentQuestion].id}-${Date.now()}`
        : `unknown-${Date.now()}`;
      setBackgroundUploads(prev => ({
        ...prev,
        [uploadId]: { ...prev[uploadId], status: 'error' }
      }));

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to submit your answer. Please try again.',
        sticky: true
      });
    }
  };

  /* -----------------------------------------------------
   * Go to Next Question
   * ----------------------------------------------------- */
  const gotoNextQuestion = async () => {
    try {
      const userId = firebase.auth().currentUser?.uid;
      if (!userId) {
        setIsNavigating(false);
        return;
      }

      await firebase
        .firestore()
        .collection(Collections.Runs)
        .doc(sessionId)
        .collection('list')
        .doc(runId)
        .update({ currentQuestion: currentQuestion + 1 });

      if (currentQuestion + 1 === sessionQuestions?.length) {
        await endSession();
        return;
      }

      setTranscript('');
      setEditedTranscript('');
      setIsEditingTranscript(false);
      setExpressions([]);
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
      setVideoUrl(null);
      setIsVideoPlayable(false);
      setCurrentQuestion(currentQuestion + 1);

      // Reset navigation state after successful navigation
      setIsNavigating(false);
    } catch (error) {
      captureException(error);

      // Reset navigation state on error
      setIsNavigating(false);

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load the next question. Please try again.',
      });
    }
  };

  /* -----------------------------------------------------
   * End Session
   * ----------------------------------------------------- */
  const endSession = async () => {
    try {
      const userId = firebase.auth().currentUser?.uid;
      if (!userId) return;
      toggleShowRunCompleteDialog(true);

      await firebase
        .firestore()
        .collection(Collections.Runs)
        .doc(sessionId)
        .collection('list')
        .doc(runId)
        .update({
          completed: true,
          status: 'Completed',
          currentQuestion: 0,
        });

      await firebase
        .firestore()
        .collection(Collections.Sessions)
        .doc(userId)
        .collection('list')
        .doc(sessionId!)
        .update({
          completed: true,
          status: 'Completed',
        });

      // Increment practice attempt usage after completing the full session
      await apiUsageService.incrementPracticeAttemptUsage();

      // Handle goal progress updates from session completion
      try {
        await sessionCompletionService.notifySessionCompletion({
          sessionId: sessionId || '',
          userId: userId,
          runId: runId || '',
          overallScore: 0, // This should be updated with actual score when available
          sessionType: 'video_interview',
          completedAt: new Date(),
          skills: {} // Can be populated with actual skill scores
        });
      } catch (goalError) {
        console.error('Error updating goals from session completion:', goalError);
        // Don't block session completion if goal update fails
      }

      setTranscript('');
      setVideoUrl(null);
    } catch (error) {
      captureException(error)
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to end the session. Please try again.',
      });
    }
  };

  /* -----------------------------------------------------
   * Skip Question Confirmation
   * ----------------------------------------------------- */
  const confirmSkipQuestion = () => {
    confirmDialog({
      message: 'Are you sure you want to skip this question? You won\'t be able to come back to it.',
      header: 'Skip Question',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        const qid = sessionQuestions?.[currentQuestion]?.id;
        if (qid) skipQuestion(qid);
      },
      reject: () => { }
    });
  };

  /* -----------------------------------------------------
   * End Session Confirmation
   * ----------------------------------------------------- */
  const confirmEndSession = () => {
    confirmDialog({
      message: 'Are you sure you want to end the session? All progress will be saved.',
      header: 'End Session',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => endSession(),
      reject: () => { }
    });
  };

  /* -----------------------------------------------------
   * Skip Question
   * ----------------------------------------------------- */
  const skipQuestion = async (questionId: number) => {
    try {
      if (!sessionQuestions) return;
      toggleSubmitting(true);

      toast.current?.show({
        severity: 'info',
        summary: 'Skipping...',
        detail: 'Moving to the next question'
      });

      const temp = [...sessionQuestions];
      const idx = temp.findIndex((q) => q.id === questionId);
      if (idx === -1) return;

      temp[idx].skipped = true;
      await firebase
        .firestore()
        .collection(Collections.Runs)
        .doc(sessionId)
        .collection('list')
        .doc(runId)
        .update({ questions: temp });

      gotoNextQuestion();
      toggleSubmitting(false);
    } catch (error) {
      toggleSubmitting(false);
      captureException(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to skip the question. Please try again.',
      });
    }
  };

  /* -----------------------------------------------------
   * Call ElevenLabs API for human-like TTS using interviewer voice
   * ----------------------------------------------------- */
  const generateSpeechWithElevenLabs = async (text: string): Promise<string> => {
    try {
      const response = await fetch('https://api.elevenlabs.io/v1/text-to-speech/' +
        (sessionData?.interviewer?.voiceId || ELEVENLABS_VOICES.rachel), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'xi-api-key': import.meta.env.VITE_ELEVEN_LABS,
        },
        body: JSON.stringify({
          text: text,
          model_id: 'eleven_multilingual_v2',
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.75,
            style: 0.0,
            use_speaker_boost: true
          }
        })
      });

      if (!response.ok) {
        throw new Error(`ElevenLabs API error: ${response.status}`);
      }

      const audioBlob = await response.blob();
      return URL.createObjectURL(audioBlob);
    } catch (error) {
      console.error('ElevenLabs API error:', error);
      throw new Error('Failed to generate speech with ElevenLabs');
    }
  };

  /* -----------------------------------------------------
   * Text-to-Speech for Questions
   * ----------------------------------------------------- */
  const readQuestionAloud = useCallback(async () => {
    if (user?.subscription.name !== Subscriptions.pro && user?.subscription.name !== Subscriptions.enterprise) {
      toast.current?.show({
        severity: 'info',
        summary: 'Premium Feature',
        detail: 'Voice playback is available on Pro and Enterprise plans.',
        life: 3000,
      });
      return;
    }

    if (!sessionQuestions) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No question available to read.',
      });
      return;
    }

    if (isReading) {
      setIsReading(false);
      setApiError(null);
      setInterviewerSpeaking(false);
      intentionalCancelRef.current = true;

      if (speechSynthesis) {
        speechSynthesis.cancel();
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      return;
    }

    try {
      const questionText = sessionQuestions[currentQuestion]?.question;
      if (!questionText) return;

      try {
        setIsLoadingVoice(true);
        setApiError(null);
        setInterviewerSpeaking(true);

        const audioUrl = await generateSpeechWithElevenLabs(questionText);

        if (!audioRef.current) {
          audioRef.current = new Audio();

          audioRef.current.addEventListener('ended', () => {
            setIsReading(false);
            setInterviewerSpeaking(false);
          });

          audioRef.current.addEventListener('error', (e) => {
            console.error('Audio playback error:', e);
            setIsReading(false);
            setInterviewerSpeaking(false);
            setApiError('Error playing audio');
            toast.current?.show({
              severity: 'error',
              summary: 'Playback Error',
              detail: 'Could not play the generated speech.',
            });
          });
        }

        audioRef.current.src = audioUrl;
        await audioRef.current.play();
        setIsReading(true);
        setIsLoadingVoice(false);

      } catch (error) {
        console.error('ElevenLabs API error, falling back to browser TTS:', error);
        setApiError('Failed to generate premium voice');
        setIsLoadingVoice(false);
        setInterviewerSpeaking(false);

        if (speechSynthesis) {
          // fallbackToBrowserTTS(questionText);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Speech Error',
            detail: 'Could not generate speech. Please try again.',
          });
        }
      }
    } catch (error) {
      console.error('Text-to-speech error:', error);
      setIsReading(false);
      setIsLoadingVoice(false);
      setInterviewerSpeaking(false);
      setApiError(null);
      toast.current?.show({
        severity: 'error',
        summary: 'Speech Error',
        detail: 'An error occurred while trying to read the question.',
      });
    }
  }, [sessionQuestions, currentQuestion, isReading, sessionData?.interviewer?.voiceId, user?.subscription.name]);

  // Start countdown before recording
  const startCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    setCountdownValue(3);
    setShowCountdown(true);

    countdownIntervalRef.current = setInterval(() => {
      setCountdownValue((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current!);
          setShowCountdown(false);
          startRecognition();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [startRecognition]);

  // Keyboard shortcuts
  useHotkeys('space', (e) => {
    e.preventDefault();
    if (isRecording) {
      stopRecognition();
    } else if (!submitting && hasPermission) {
      startCountdown();
    }
  }, [isRecording, submitting, hasPermission, stopRecognition, startCountdown]);

  useHotkeys('esc', (e) => {
    e.preventDefault();
    if (isRecording) {
      stopRecognition();
    }
    if (isReading) {
      readQuestionAloud();
    }
    if (showRunCompleteDialog) {
      toggleShowRunCompleteDialog(false);
    }
  }, [isRecording, isReading, showRunCompleteDialog, stopRecognition, readQuestionAloud]);

  useHotkeys('enter', (e) => {
    if (videoUrl && !submitting && isVideoPlayable) {
      e.preventDefault();
      submitAnswer();
    }
  }, [videoUrl, submitting, isVideoPlayable, submitAnswer]);

  // Handle permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setIsCheckingPermission(true);
      try {
        // First check if there's already an active stream in the webcam component
        if (webcamRef.current?.stream) {
          setHasPermission(true);
          setupAudioAnalyzer(webcamRef.current.stream);
          setIsCheckingPermission(false);
          return;
        }

        // Otherwise request a new stream
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        setHasPermission(true);
        setupAudioAnalyzer(stream);

        // Release this temporary stream since Webcam will create its own
        stream.getTracks().forEach(track => track.stop());
      } catch (error) {
        console.error("Permission error:", error);
        setHasPermission(false);
        setCameraErrorMsg("Camera access denied");
        setMicrophoneErrorMsg("Microphone access denied");
        setPermissionDialogVisible(true);
      } finally {
        setIsCheckingPermission(false);
      }
    };

    checkPermissions();

    return () => {
      // Clean up audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Setup audio analyzer helper function with real-time updates
  const setupAudioAnalyzer = useCallback((stream: MediaStream) => {
    // Setup audio analyzer for volume visualization
    if (!audioContextRef.current) {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;

      const audioSource = audioContext.createMediaStreamSource(stream);
      audioSource.connect(analyser);

      audioContextRef.current = audioContext;
      audioAnalyserRef.current = analyser;
      audioDataRef.current = new Uint8Array(analyser.frequencyBinCount);

      // Start monitoring audio levels
      const updateAudioLevel = () => {
        if (audioAnalyserRef.current && audioDataRef.current) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          audioAnalyserRef.current.getByteTimeDomainData(audioDataRef.current as any);

          let sum = 0;
          for (let i = 0; i < audioDataRef.current.length; i++) {
            const value = (audioDataRef.current[i] - 128) / 128;
            sum += value * value;
          }
          const rms = Math.sqrt(sum / audioDataRef.current.length);
          const threshold = 0.01;
          setIsSpeaking(rms > threshold);

          // Track audio level (0-100)
          const normalizedLevel = Math.min(rms * 1000, 100);
          setAudioLevel(normalizedLevel);

          if (isRecording && !isPaused) {
            requestAnimationFrame(updateAudioLevel);
          }
        }
      };

      if (isRecording && !isPaused) {
        requestAnimationFrame(updateAudioLevel);
      }
    }
  }, [isRecording, isPaused, startRecognition]);

  // Render the permission dialog
  const renderPermissionDialog = () => {
    return (
      <Dialog
        header="Camera & Microphone Access Required"
        visible={permissionDialogVisible}
        onHide={() => setPermissionDialogVisible(false)}
        closable
        style={{ width: '90%', maxWidth: '500px' }}
        footer={
          <div className="flex justify-between">
            <Button
              label="Go Back"
              className="p-button-outlined"
              onClick={() => navigate(-1)}
            />
            <Button
              label="Try Again"
              className="p-button-primary"
              onClick={() => {
                setPermissionDialogVisible(false);
                // Re-request permissions
                navigator.mediaDevices.getUserMedia({ video: true, audio: true })
                  .then(() => {
                    setHasPermission(true);
                    window.location.reload();
                  })
                  .catch(() => {
                    setPermissionDialogVisible(true);
                  });
              }}
            />
          </div>
        }
      >
        <div className="flex flex-col items-center">
          <div className="mt-2 mb-4 text-center">
            <MdWarning className="text-red-500 text-5xl mx-auto mb-2" />
            <p className="text-gray-700">
              This interview session requires access to your camera and microphone.
              Please allow access when prompted by your browser.
            </p>
          </div>

          <div className="w-full p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-700 mb-2">How to enable permissions:</h3>
            <ol className="list-decimal list-inside text-gray-600 space-y-2">
              <li>Click the padlock or info icon in your browser's address bar</li>
              <li>Find "Camera" and "Microphone" settings</li>
              <li>Change both settings to "Allow"</li>
              <li>Refresh this page</li>
            </ol>
          </div>

          {cameraErrorMsg && (
            <div className="mt-3 text-red-500 text-sm">{cameraErrorMsg}</div>
          )}
          {microphoneErrorMsg && (
            <div className="mt-1 text-red-500 text-sm">{microphoneErrorMsg}</div>
          )}
        </div>
      </Dialog>
    );
  };

  // Add this new component for the upload info dialog
  const UploadInfoDialog = () => {
    const activeUploads = Object.entries(backgroundUploads).filter(
      ([, upload]) => upload.status === 'uploading'
    );

    if (!showUploadInfo || activeUploads.length === 0) return null;

    return (
      <Dialog
        header="Background Uploads"
        visible={showUploadInfo}
        onHide={() => setShowUploadInfo(false)}
        style={{ width: '90%', maxWidth: '500px' }}
        className="bg-white rounded-lg shadow-xl"
      >
        <div className="p-4">
          <p className="text-gray-600 mb-4">
            Your previous answer is still being uploaded. You can continue with the next question while it processes in the background.
          </p>

          {activeUploads.map(([id, upload]) => (
            <div key={id} className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Question {id.split('-')[0]}
                </span>
                <span className="text-sm text-gray-500">
                  {upload.progress}%
                </span>
              </div>
              <ProgressBar
                value={upload.progress}
                className="h-5"
                showValue={false}
              />
            </div>
          ))}

          <div className="mt-4 flex justify-end">
            <Button
              label="Continue"
              className="p-button-primary"
              onClick={() => setShowUploadInfo(false)}
            />
          </div>
        </div>
      </Dialog>
    );
  };


  // Auto-read question when loaded or changed (only if enabled)
  useEffect(() => {
    // Only auto-read if enabled, we have session questions, the question index changed, premium plan, and not already speaking
    if (
      autoReadEnabled &&
      sessionQuestions &&
      currentQuestion !== hasAutoReadRef.current &&
      !isReading &&
      !isLoadingVoice &&
      (user?.subscription.name === Subscriptions.pro || user?.subscription.name === Subscriptions.enterprise)
    ) {
      // Mark this question as read to prevent reading again on re-renders
      hasAutoReadRef.current = currentQuestion;

      // Small delay to ensure UI is fully rendered
      const timer = setTimeout(() => {
        readQuestionAloud();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [autoReadEnabled, currentQuestion, sessionQuestions, readQuestionAloud, isReading, isLoadingVoice, user?.subscription.name]);

  // Reset the auto-read flag when moving to a new question
  useEffect(() => {
    return () => {
      // When unmounting or changing questions, cancel any ongoing speech
      if (isReading) {
        // Stop browser speech synthesis if it's being used
        if (speechSynthesis) {
          speechSynthesis.cancel();
        }

        // Stop API-based audio if it's playing
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
      }
    };
  }, [currentQuestion, isReading]);

  // Add back the cleanup on component unmount
  useEffect(() => {
    return () => {
      // Stop any playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Cancel any speech synthesis
      if (speechSynthesis) {
        speechSynthesis.cancel();
      }
    };
  }, []);

  // Add sound wave animations
  useEffect(() => {
    // Create a style element
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      @keyframes sound-wave-1 {
        0% { height: 3px; }
        50% { height: 10px; }
        100% { height: 3px; }
      }
      @keyframes sound-wave-2 {
        0% { height: 5px; }
        50% { height: 15px; }
        100% { height: 5px; }
      }
      @keyframes sound-wave-3 {
        0% { height: 7px; }
        50% { height: 20px; }
        100% { height: 7px; }
      }
      .animate-sound-wave-1 {
        animation: sound-wave-1 1s ease-in-out infinite;
      }
      .animate-sound-wave-2 {
        animation: sound-wave-2 0.8s ease-in-out infinite;
      }
      .animate-sound-wave-3 {
        animation: sound-wave-3 1.2s ease-in-out infinite;
      }
    `;
    document.head.appendChild(styleEl);

    // Clean up on unmount
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // Loading skeleton
  if (isLoading || !sessionData || !sessionQuestions) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <div className="sticky top-0 z-40 bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 sm:p-4">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="h-8 w-16 bg-white bg-opacity-20 rounded animate-pulse"></div>
            <div className="h-6 w-48 bg-white bg-opacity-20 rounded animate-pulse"></div>
            <div className="h-8 w-24 bg-white bg-opacity-20 rounded animate-pulse"></div>
          </div>
        </div>
        <main className="flex-1 p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg space-y-4">
            <div className="h-24 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg space-y-4">
            <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-48 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </main>
      </div>
    );
  }

  // Loading state
  if (submitting) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-tl from-blue-50 to-white">
        <DotLottieReact
          src="https://lottie.host/7d99b882-cefc-4168-8f45-18871e5d7847/EJdZ6syq9P.lottie"
          loop
          autoplay
          className="w-60 h-60"
        />
        <p className="text-gray-600 mt-4">Uploading your response...</p>
        {/* {uploadProgress > 0 && (
          <div className="w-64 mt-4">
            <ProgressBar value={uploadProgress} className="h-5" />
            <p className="text-center text-sm text-gray-500 mt-2">{uploadProgress}% Uploaded</p>
          </div>
        )} */}
      </div>
    );
  }

  // Permission checking state
  if (isCheckingPermission) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-tl from-blue-50 to-white">
        <DotLottieReact
          src="https://lottie.host/64e87f80-295d-49e4-9c64-1eabe588ff0d/iRUAvvLstB.lottie"
          loop
          autoplay
          className="w-40 h-40"
        />
        <p className="text-gray-600 mt-4">Checking camera and microphone permissions...</p>
      </div>
    );
  }

  // Permission denied state
  if (hasPermission === false) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col relative p-6">
        <div className="max-w-lg mx-auto bg-white rounded-lg shadow-lg p-8 mt-10">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <MdWarning className="text-red-500 text-3xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Permission Required</h2>
          </div>

          <p className="text-gray-600 mb-6">
            This interview session requires access to your camera and microphone.
            Please enable permissions in your browser settings and refresh the page.
          </p>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold text-gray-700 mb-2">How to enable permissions:</h3>
            <ol className="list-decimal list-inside text-gray-600 space-y-2">
              <li>Click the padlock or info icon in your browser's address bar</li>
              <li>Find "Camera" and "Microphone" settings</li>
              <li>Change both settings to "Allow"</li>
              <li>Refresh this page</li>
            </ol>
          </div>

          <div className="mt-8 flex justify-between">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col relative">
      <Toast ref={toast} />
      <ConfirmDialog />
      {renderPermissionDialog()}
      <UploadInfoDialog />

      {/* ---------- HEADER (Sticky, Mobile-optimized) ---------- */}
      <header className="sticky top-0 z-40 bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 sm:p-4 shadow-lg">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Back Button */}
          <motion.button
            onClick={() => navigate(-1)}
            className="flex items-center text-white hover:text-gray-200 transition p-2 sm:px-3 sm:py-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={submitting || isRecording}
            aria-label="Go back"
          >
            <FaArrowLeft className="text-base sm:text-lg" />
            <span className="hidden sm:inline ml-2 text-sm font-medium">Back</span>
          </motion.button>

          {/* Title & Timer */}
          <div className="flex-1 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 sm:gap-4 min-w-0">
            <motion.h1
              className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-center truncate max-w-full"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              title={sessionData?.title || 'Interview Session'}
            >
              {sessionData?.title || 'Interview Session'}
            </motion.h1>

            {/* Timer - Always visible, compact on mobile */}
            <div className="flex items-center text-xs sm:text-sm bg-purple-700 bg-opacity-30 px-2 sm:px-3 py-1 rounded-full">
              <FaStopwatch className="mr-1 sm:mr-2 text-purple-200" />
              <Countdown
                startSeconds={timeRemaining || 0}
                dontCountSignal={showRunCompleteDialog || submitting}
                sessionId={sessionData.id || ''}
                runId={runId || ''}
                onComplete={endSession}
                interviewType={INTERVIEW_TYPES.VIDEO_INTERVIEW}
              />
            </div>
          </div>

          {/* End Session Button */}
          <motion.button
            onClick={confirmEndSession}
            className="bg-red-500 px-3 sm:px-4 py-2 rounded-lg hover:bg-red-600 transition flex items-center text-xs sm:text-sm font-semibold min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 justify-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={submitting || isRecording}
            aria-label="End session"
          >
            <span className="hidden sm:inline">End Session</span>
            <span className="sm:hidden">End</span>
          </motion.button>
        </div>
      </header>

      {/* ---------- MAIN CONTENT ---------- */}
      <main className="flex-1 p-3 md:p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* ----- INTERVIEWER PANEL ----- */}
        <motion.section
          className="bg-white p-4 md:p-6 rounded-lg shadow-lg flex flex-col"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Interviewer Profile Card - Mobile optimized */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 gap-3 sm:gap-4">
            <div className="relative flex-shrink-0">
              <img
                src={sessionData?.interviewer?.image || 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=300&h=300&auto=format&fit=crop'}
                alt={sessionData?.interviewer?.name || 'Interviewer'}
                className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 ${interviewerSpeaking ? 'border-blue-500 animate-pulse' : 'border-gray-300'}`}
              />
              {interviewerSpeaking && (
                <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-1">
                  <FaVolumeUp className="text-white text-xs" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold truncate">
                {sessionData?.interviewer?.name || 'Your Interviewer'}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {sessionData?.interviewer?.title || 'Interview Session'}
              </p>
            </div>

            {/* Question Counter - More prominent, better mobile layout */}
            <div className="w-full sm:w-auto sm:ml-auto">
              <div className="flex items-center justify-between sm:justify-end text-xs sm:text-sm text-gray-600 mb-1">
                <span className="font-semibold">
                  Question {currentQuestion + 1} of {sessionQuestions?.length || 0}
                </span>
              </div>
              <div className="relative h-2 w-full sm:w-32 bg-gray-200 rounded-full">
                <motion.div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentQuestion + 1) / (sessionQuestions?.length || 1)) * 100}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              </div>
            </div>
          </div>

          {/* Conversation/Question Bubble - Fixed overlap issue */}
          <div className="mb-4 flex-1">
            <div className={`p-4 sm:p-6 rounded-lg bg-blue-50 border-l-4 border-blue-500 relative transition-all ${interviewerSpeaking ? 'shadow-md border-blue-600' : ''}`}>
              {/* Voice control button - Better positioning, larger touch target */}
              <div className="absolute top-3 right-3 flex items-center gap-2">
                {apiError && (
                  <span className="text-xs text-red-500 hidden sm:inline">
                    {apiError === 'Failed to generate premium voice' ? 'Using fallback' : ''}
                  </span>
                )}
                <motion.button
                  onClick={readQuestionAloud}
                  className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full ${isLoadingVoice
                    ? 'bg-gray-400 text-white'
                    : isReading
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-200 text-gray-700'
                    } hover:bg-purple-600 hover:text-white transition`}
                  whileHover={!isLoadingVoice ? { scale: 1.1 } : {}}
                  whileTap={!isLoadingVoice ? { scale: 0.95 } : {}}
                  aria-label={isReading ? "Stop reading question" : "Read question aloud"}
                  title={isReading ? "Stop reading question" : "Read question aloud"}
                  disabled={isLoadingVoice}
                >
                  {isLoadingVoice ? (
                    <FaSpinner className="animate-spin text-base sm:text-lg" />
                  ) : isReading ? (
                    <FaStop className="text-base sm:text-lg" />
                  ) : (
                    <FaVolumeUp className="text-base sm:text-lg" />
                  )}
                </motion.button>
              </div>
              <div className="pr-16 sm:pr-20">
                <p className={`text-gray-800 text-base sm:text-lg md:text-xl leading-relaxed ${interviewerSpeaking ? 'text-blue-700 font-medium' : ''}`}>
                  {sessionQuestions?.[currentQuestion]?.question || 'Loading question...'}
                </p>
              </div>

              {/* Visual indicator for speech */}
              {interviewerSpeaking && (
                <div className="mt-4 flex justify-center">
                  <div className="flex items-end space-x-1">
                    <div className="w-1 h-3 bg-blue-400 rounded-full animate-sound-wave-1"></div>
                    <div className="w-1 h-5 bg-blue-500 rounded-full animate-sound-wave-2"></div>
                    <div className="w-1 h-7 bg-blue-600 rounded-full animate-sound-wave-3"></div>
                    <div className="w-1 h-5 bg-blue-500 rounded-full animate-sound-wave-2"></div>
                    <div className="w-1 h-3 bg-blue-400 rounded-full animate-sound-wave-1"></div>
                  </div>
                </div>
              )}
            </div>

            {/* Tips section - Collapsible */}
            <div className="mt-4 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
              <motion.button
                onClick={() => setShowTips(!showTips)}
                className="w-full p-3 sm:p-4 flex items-center justify-between font-semibold text-gray-700 text-sm uppercase hover:bg-gray-100 transition"
                whileTap={{ scale: 0.98 }}
                aria-label={showTips ? "Hide tips" : "Show tips"}
              >
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Tips from your interviewer</span>
                </div>
                {showTips ? <FaChevronUp /> : <FaChevronDown />}
              </motion.button>
              <AnimatePresence>
                {showTips && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <ul className="p-3 sm:p-4 pt-0 list-disc list-inside text-xs sm:text-sm text-gray-600 space-y-1.5">
                      <li>Be concise and specific in your response</li>
                      <li>Use examples from your experience</li>
                      <li>Structure your answer (situation, task, action, result)</li>
                      <li>Remember to speak clearly and maintain good posture</li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Auto-read toggle */}
            <div className="mt-3 flex items-center justify-between text-xs sm:text-sm">
              <label className="flex items-center text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoReadEnabled}
                  onChange={(e) => setAutoReadEnabled(e.target.checked)}
                  className="mr-2 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span>Auto-read questions</span>
              </label>
            </div>
          </div>
        </motion.section>

        {/* ----- RECORDING / CAMERA FEED PANEL ----- */}
        <motion.section
          className="flex flex-col space-y-4"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Camera Feed - Your side of the conversation */}
          <motion.div
            className="bg-white p-3 sm:p-4 rounded-lg shadow-lg flex-1 flex flex-col relative"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex justify-between items-center mb-2">
              <label className="text-base sm:text-lg font-bold text-gray-800 flex items-center">
                <FaCamera className="mr-2 text-blue-500" /> Your Camera
              </label>
              {isRecording && (
                <span className="flex items-center text-xs sm:text-sm">
                  <span className="animate-pulse h-2 w-2 sm:h-3 sm:w-3 bg-red-500 rounded-full mr-2"></span>
                  {isPaused ? "Paused" : "Recording"}
                </span>
              )}
            </div>

            <div className="flex-1 flex items-center justify-center bg-black rounded-lg overflow-hidden relative aspect-video min-h-[200px] sm:min-h-[300px]">
              <Webcam
                audio
                className="w-full h-full rounded-lg object-cover"
                ref={webcamRef}
                muted
                mirrored
              />

              {/* Audio Level Indicator (when recording) */}
              {isRecording && !isPaused && (
                <div className="absolute top-2 left-2 flex items-center gap-2 bg-black bg-opacity-50 rounded px-2 py-1">
                  <div className="w-1 h-4 bg-gray-600 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${audioLevel > 30 ? 'bg-green-500' : audioLevel > 10 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${audioLevel}%` }}
                    />
                  </div>
                  {isSpeaking && (
                    <span className="text-white text-xs">Speaking</span>
                  )}
                </div>
              )}

              {/* Countdown overlay */}
              <AnimatePresence>
                {showCountdown && (
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <motion.div
                      className="bg-white text-gray-900 font-bold text-4xl sm:text-5xl w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center rounded-full"
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.5, opacity: 0 }}
                      key={countdownValue}
                    >
                      {countdownValue}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {videoUrl && (
              <div className="mt-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-800 flex items-center">
                  <FaCameraRetro className="mr-2 text-purple-500" /> Review Recording
                </h3>
                <video
                  src={videoUrl}
                  controls
                  className="w-full mt-2 rounded-lg border-2 border-purple-200 aspect-video bg-black"
                  onLoadedData={() => setIsVideoPlayable(true)}
                />
              </div>
            )}
          </motion.div>

          {/* Recording Controls */}
          <motion.div
            className="bg-white p-4 sm:p-6 rounded-lg shadow-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex flex-col space-y-4">
              {/* Buttons - Larger touch targets */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                {!isRecording && (
                  <motion.button
                    onClick={startCountdown}
                    className="flex-1 bg-blue-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-blue-600 transition flex items-center justify-center shadow-md min-h-[44px] font-semibold text-sm sm:text-base"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={submitting}
                  >
                    <FaMicrophone className="mr-2 text-base sm:text-lg" />
                    <span className="hidden sm:inline">Start Recording</span>
                    <span className="sm:hidden">Record</span>
                    <div className="ml-2 text-xs bg-white bg-opacity-20 px-2 py-1 rounded hidden sm:inline">
                      Space
                    </div>
                  </motion.button>
                )}

                {isRecording && (
                  <>
                    <motion.button
                      onClick={stopRecognition}
                      className="flex-1 bg-red-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-red-600 transition flex items-center justify-center shadow-md min-h-[44px] font-semibold text-sm sm:text-base"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FaStop className="mr-2 text-base sm:text-lg" />
                      <span className="hidden sm:inline">Stop Recording</span>
                      <span className="sm:hidden">Stop</span>
                      <div className="ml-2 text-xs bg-white bg-opacity-20 px-2 py-1 rounded hidden sm:inline">
                        Space
                      </div>
                    </motion.button>

                    <motion.button
                      onClick={togglePauseRecording}
                      className={`flex-1 ${isPaused ? 'bg-green-500 hover:bg-green-600' : 'bg-yellow-500 hover:bg-yellow-600'} text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg transition flex items-center justify-center shadow-md min-h-[44px] font-semibold text-sm sm:text-base`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isPaused ? (
                        <>
                          <IoMdPlay className="mr-2 text-base sm:text-lg" />
                          Resume
                        </>
                      ) : (
                        <>
                          <IoMdPause className="mr-2 text-base sm:text-lg" />
                          Pause
                        </>
                      )}
                    </motion.button>
                  </>
                )}
              </div>

              {/* Recording Countdown - Color-coded */}
              {isRecording && (
                <motion.div
                  className={`font-semibold flex items-center justify-center bg-gray-50 py-2 sm:py-3 px-4 rounded-full text-sm sm:text-base ${recordingTimeLeft <= 30 ? recordingTimeLeft <= 10 ? 'text-red-600 bg-red-50' : 'text-orange-600 bg-orange-50' : 'text-gray-800'}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <FaStopwatch className={`mr-2 ${recordingTimeLeft <= 10 && !isPaused ? 'animate-pulse' : ''} ${isPaused ? 'text-yellow-500' : ''}`} />
                  Recording time left:{' '}
                  <span className={`ml-2 ${isPaused ? 'text-yellow-500' : ''}`}>
                    {recordingTimeLeft <= 10 && !isPaused && '⚠️ '}
                    {formatTime(recordingTimeLeft)}
                  </span>
                </motion.div>
              )}

              {/* Transcript - Editable, larger area */}
              <motion.div
                className="mt-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <div className="flex justify-between items-center mb-2">
                  <p className="font-bold text-gray-800 flex items-center text-sm sm:text-base">
                    <FaMicrophone className={isListening ? "mr-2 text-green-500" : "mr-2 text-gray-400"} />
                    Your Response:
                  </p>
                  <div className="flex items-center gap-2">
                    {isRecording && isListening && !isPaused && (
                      <span className="text-xs text-green-500 animate-pulse">Live transcription</span>
                    )}
                    {!isRecording && transcript && (
                      <motion.button
                        onClick={() => {
                          setIsEditingTranscript(!isEditingTranscript);
                          if (!isEditingTranscript) {
                            setEditedTranscript(transcript);
                          }
                        }}
                        className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                        whileTap={{ scale: 0.95 }}
                      >
                        {isEditingTranscript ? (
                          <>
                            <FaCheck className="text-green-600" />
                            <span className="hidden sm:inline">Done</span>
                          </>
                        ) : (
                          <>
                            <FaEdit />
                            <span className="hidden sm:inline">Edit</span>
                          </>
                        )}
                      </motion.button>
                    )}
                  </div>
                </div>
                {isEditingTranscript ? (
                  <textarea
                    value={editedTranscript}
                    onChange={(e) => setEditedTranscript(e.target.value)}
                    className="w-full min-h-[120px] sm:min-h-[150px] max-h-[200px] text-gray-700 whitespace-pre-wrap p-4 rounded-lg bg-white border-2 border-indigo-300 focus:border-indigo-500 focus:outline-none resize-y text-sm sm:text-base"
                    placeholder="Edit your transcript here..."
                  />
                ) : (
                  <div className="min-h-[120px] sm:min-h-[150px] max-h-[200px] text-gray-700 whitespace-pre-wrap p-4 rounded-lg bg-gray-50 border-r-4 border-gray-300 overflow-y-auto text-sm sm:text-base">
                    {transcript || 'Your speech will appear here when you start recording...'}
                  </div>
                )}
              </motion.div>

              {/* Submit Answer Button (hidden until a videoUrl exists) */}
              <AnimatePresence>
                {videoUrl && isVideoPlayable && (
                  <motion.div
                    className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  >
                    <motion.button
                      onClick={startCountdown}
                      className="bg-gray-200 text-gray-700 px-4 sm:px-6 py-3 sm:py-4 rounded-lg hover:bg-gray-300 transition flex items-center justify-center shadow-md min-h-[44px] font-semibold text-sm sm:text-base order-2 sm:order-1"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={submitting}
                    >
                      <FaRedo className="mr-2" />
                      Re-record
                    </motion.button>

                    <motion.button
                      onClick={() => submitAnswer()}
                      className="bg-green-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-green-600 transition flex items-center justify-center shadow-md min-h-[44px] font-semibold text-sm sm:text-base order-1 sm:order-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={submitting}
                    >
                      <MdCheck className="mr-2 text-base sm:text-lg" />
                      Submit Answer
                      <div className="ml-2 text-xs bg-white bg-opacity-20 px-2 py-1 rounded hidden sm:inline">
                        Enter
                      </div>
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.section>
      </main>

      {/* ---------- FOOTER (Enhanced) ---------- */}
      <footer className="p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 bg-white shadow-inner border-t border-gray-200">
        <motion.button
          onClick={confirmSkipQuestion}
          className="flex items-center text-gray-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-200 transition text-sm font-semibold min-w-[120px] min-h-[44px] justify-center"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={submitting || isRecording}
          aria-label="Skip question"
        >
          <MdSkipNext className="mr-2" />
          Skip Question
        </motion.button>

        <div className="text-xs sm:text-sm text-gray-500">
          <span className="hidden sm:inline">Keyboard shortcuts: </span>
          <span className="font-mono bg-gray-100 px-2 py-1 rounded">Space</span> to record,{' '}
          <span className="font-mono bg-gray-100 px-2 py-1 rounded">Enter</span> to submit
        </div>
      </footer>

      {/* ---------- COMPLETION OVERLAY ---------- */}
      <AnimatePresence>
        {showRunCompleteDialog && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => toggleShowRunCompleteDialog(false)}
          >
            {/* Confetti */}
            <div className="absolute inset-0 pointer-events-none">
              <Confetti width={width} height={height} />
            </div>

            <motion.div
              className="relative bg-white rounded-lg shadow-lg p-6 sm:p-8 text-center flex flex-col items-center max-w-md w-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => toggleShowRunCompleteDialog(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl"
                aria-label="Close"
              >
                ×
              </button>
              <DotLottieReact
                src="https://lottie.host/392d3f6c-7239-46fc-9b41-ca5a9814504d/hFXbyazAMW.lottie"
                loop
                autoplay
                className="w-32 sm:w-40 h-32 sm:h-40 mx-auto mb-4"
              />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
                Session Complete!
              </h2>
              <p className="text-gray-600 mb-6 text-xs sm:text-sm">
                Congratulations on completing your session!
                Our AI is analyzing your performance to provide personalized insights and goals.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <motion.button
                  onClick={() => navigate(-1)}
                  className="bg-purple-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-purple-600 transition font-semibold flex-1 min-h-[44px] text-sm sm:text-base"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Go to Dashboard
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VideoActiveSession;
